import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { GraduationCap, ArrowRight, CalendarDays, FileText, Scale, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import MainSiteLink from "@/components/MainSiteLink";

const HERO_IMG =
  "https://images.pexels.com/photos/6549588/pexels-photo-6549588.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">{t("brand")}</span>
        </div>
        <MainSiteLink />
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center pt-8 pb-16">
        <div className="animate-fade-in">
          <p className="text-sm text-accent uppercase tracking-[0.2em] font-medium mb-4">{t("landing.academic_year")}</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
            <Trans i18nKey="landing.title" components={{ hl: <span className="text-primary" /> }} />
          </h1>
          <p className="text-muted-foreground leading-relaxed mt-6 text-lg">{t("landing.intro")}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/apply" data-testid="start-application-btn">
              <Button className="h-12 px-7 text-base rounded-lg group">
                {t("landing.start")}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/apply" data-testid="my-application-btn">
              <Button variant="outline" className="h-12 px-7 text-base rounded-lg">
                {t("landing.view_edit")}
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-6 mt-10 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4" /> {t("landing.deadline", { date: "30.09.2026" })}</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {t("landing.free")}</span>
          </div>
        </div>
        <div className="relative animate-fade-in">
          <img
            src={HERO_IMG}
            alt={t("landing.hero_alt")}
            className="rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
          />
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-6xl mx-auto px-6 pb-20 grid sm:grid-cols-3 gap-6">
        {[
          { icon: FileText, key: "apply" },
          { icon: Scale, key: "review" },
          { icon: CalendarDays, key: "interview" },
        ].map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-2xl p-8 hover:shadow-sm transition-shadow">
            <div className="w-11 h-11 rounded-lg bg-secondary flex items-center justify-center mb-5">
              <s.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl font-medium mb-2">{t(`landing.steps.${s.key}.title`)}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{t(`landing.steps.${s.key}.desc`)}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
